import { toast } from "@dafiti/closet-ui";
import { useCoreLayoutManager } from "@dafiti/core-layout";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocale, useTranslations } from "next-intl";
import { useEnvContext } from "next-runtime-env";

import {
  CheckWishlistedResponse,
  ToggleWishlistResponse,
  checkIsWishlistedQueryOptions,
  toggleWishlistMutationOptions,
} from "../api/wishlist.query";

export const WISHLIST_INTENT_PARAM = "addToWishlist";

type Props = {
  sku: string;
  enabled?: boolean;
  initialIsWishlisted?: boolean;
  onToggleSuccess?: (response: ToggleWishlistResponse) => void;
};

export function useAddToWishlist({
  sku,
  enabled,
  initialIsWishlisted,
  onToggleSuccess,
}: Props) {
  const t = useTranslations("wishlist");
  const locale = useLocale();
  const {
    NEXT_PUBLIC_BFF_API: apiOrigin = "",
    NEXT_PUBLIC_BASE_DOMAIN: baseDomain = "",
    NEXT_PUBLIC_LOGIN_URL: loginUrl,
  } = useEnvContext();

  if (!loginUrl) {
    console.error("NEXT_PUBLIC_LOGIN_URL is not configured");
  }
  const queryClient = useQueryClient();
  const { updateWishlist } = useCoreLayoutManager();

  const { data: isWishlisted, isLoading } = useQuery({
    ...checkIsWishlistedQueryOptions({ sku, apiOrigin, locale }),
    enabled,
    initialData: initialIsWishlisted,
  });

  const mutation = useMutation({
    ...toggleWishlistMutationOptions({
      sku,
      apiOrigin,
      isWishlisted: isWishlisted || false,
      locale,
    }),
    onMutate: () => {
      if (!isWishlisted) {
        updateWishlist(true);
      }

      queryClient.setQueryData<CheckWishlistedResponse>(
        checkIsWishlistedQueryOptions({ sku, apiOrigin, locale }).queryKey,
        !isWishlisted,
      );

      return { previousWishlisted: isWishlisted };
    },
    onError: (_, __, context) => {
      toast({
        message: t("toast.unknownError"),
        type: "error",
        icon: true,
        position: "top-right",
      });

      queryClient.setQueryData<CheckWishlistedResponse>(
        checkIsWishlistedQueryOptions({ sku, apiOrigin, locale }).queryKey,
        context?.previousWishlisted ?? !isWishlisted,
      );
    },
    onSuccess: (response) => {
      if ("has_products" in response && !response.has_products) {
        updateWishlist(response.has_products);
      }

      // Toggling from the PDP changes the user's wishlist contents, so the
      // /meus-favoritos list queries (paginated + infinite) must be marked
      // stale — otherwise staleTime keeps serving an outdated cached list.
      queryClient.invalidateQueries({ queryKey: ["wishlist-products-page"] });
      queryClient.invalidateQueries({
        queryKey: ["wishlist-products-infinite"],
      });

      onToggleSuccess?.(response);
    },
  });

  const currentPageUrl =
    typeof window !== "undefined"
      ? `${baseDomain}${window.location.pathname}`
      : baseDomain;

  const returnUrl = `${currentPageUrl}?${WISHLIST_INTENT_PARAM}=${encodeURIComponent(sku)}`;

  return {
    isWishlisted: isWishlisted,
    toggleWishlisted: mutation.mutateAsync,
    isLoading,
    fallbackUrl: `${loginUrl ?? ""}?returnUrl=${encodeURIComponent(returnUrl)}`,
    isRequestPending: mutation.status === "pending",
  };
}
